import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ArrowDown, ChevronLeft, Lock, MessagesSquare, RotateCw, Send, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import EmptyState from "@/components/common/EmptyState";
import UserAvatar from "@/components/common/UserAvatar";
import { cn } from "@/utils/common.utils";
import {
  flattenMessages,
  useChatMessages,
  useChatThreads,
  useMarkChatRead,
  useSendChatMessage,
} from "@/hooks/useDealChat";
import { useChatThreadOnScreen } from "@/lib/chatSocket";
import type { ChatMessage, ChatSocketStatus, ChatThread } from "@/types/chat.types";

/** Mirrors `chat_service.MESSAGE_MAX_CHARS` on the server. */
const MESSAGE_MAX_CHARS = 4000;
const COUNTER_FROM = MESSAGE_MAX_CHARS - 500;
/** Within this many pixels of the bottom counts as "reading the latest". */
const NEAR_BOTTOM_PX = 120;

// Unsent text survives switching threads or tabs; it is lost only on reload.
const drafts = new Map<string, string>();

export interface ChatViewer {
  id: string;
  name: string;
  /** `agent` for the host team, otherwise the viewer's participant role. */
  role: string;
  internal: boolean;
}

// Where the conversation list and the open conversation sit side by side.
// Below it they take turns, with a back button. Spelled out in full because
// Tailwind only generates classes it can see as literal strings.
const SPLIT_CLASSES = {
  lg: { list: "lg:flex lg:w-72 lg:shrink-0 lg:border-r", pane: "lg:flex", back: "lg:hidden" },
  "2xl": { list: "2xl:flex 2xl:w-72 2xl:shrink-0 2xl:border-r", pane: "2xl:flex", back: "2xl:hidden" },
} as const;

interface DealChatProps {
  dealId: string;
  viewer: ChatViewer;
  socketStatus: ChatSocketStatus;
  /** Internal only: opens the invite dialog from the empty state. */
  onInvite?: () => void;
  className?: string;
  /** Breakpoint for side-by-side list + conversation. The Chats page already
   *  spends a column on its deal list, so it splits later. */
  splitAt?: keyof typeof SPLIT_CLASSES;
}

// ============ Formatting ============
function timeOf(iso: string) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function dayKey(iso: string) {
  const date = new Date(iso);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dayLabel(iso: string) {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return "Today";
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return "Yesterday";
  return date.toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    ...(date.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  });
}

export function previewTime(iso?: string) {
  if (!iso) return "";
  return dayKey(iso) === dayKey(new Date().toISOString()) ? timeOf(iso) : dayLabel(iso);
}

const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

/** Plain text with bare links made clickable. No HTML is ever interpreted. */
function Linkified({ text, mine }: { text: string; mine: boolean }) {
  const parts = text.split(URL_PATTERN);
  return (
    <>
      {parts.map((part, i) => (i % 2 === 1
        ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className={cn("underline underline-offset-2 break-all", mine ? "text-white" : "text-dash-greenDeep")}>
            {part}
          </a>
        )
        : <Fragment key={i}>{part}</Fragment>))}
    </>
  );
}

function privacyLine(thread: ChatThread, viewer: ChatViewer) {
  if (thread.is_mine) {
    return thread.subtitle === "Your agent"
      ? "Private between you and your agent"
      : "Private between you and your agent’s team";
  }
  return viewer.internal ? `Private between your team and ${thread.title}` : `Private between you and ${thread.title}`;
}

function StatusPill({ status }: { status: ChatSocketStatus }) {
  const live = status === "open";
  const label = live ? "Live" : status === "closed" ? "Offline" : "Reconnecting…";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium",
        live ? "bg-dash-hover text-dash-greenDeep" : "bg-dash-amber/10 text-dash-amber",
      )}
      title={live ? "New messages arrive instantly" : "Messages still send; new ones are checked every few seconds until the connection returns"}
      data-testid="chat-connection-status"
    >
      <span className={cn("size-1.5 rounded-full", live ? "bg-dash-greenDeep" : "bg-dash-amber animate-pulse")} aria-hidden="true" />
      {label}
    </span>
  );
}

// ============ Thread list ============
function ThreadList({ threads, activeId, viewerId, onSelect }: {
  threads: ChatThread[];
  activeId: string | null;
  viewerId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="chat-thread-list">
      {threads.map((thread) => {
        const active = thread.id === activeId;
        const last = thread.last_message;
        const preview = last ? `${last.sender_id === viewerId ? "You: " : ""}${last.body}` : "No messages yet";
        return (
          <li key={thread.id}>
            <button
              type="button"
              onClick={() => onSelect(thread.id)}
              className={cn(
                "flex w-full items-start gap-3 border-b border-dash-border px-4 py-3.5 text-left transition-colors",
                active ? "bg-dash-hover" : "hover:bg-dash-searchBg",
              )}
              aria-current={active ? "true" : undefined}
              data-testid={`chat-thread-${thread.id}`}
            >
              <UserAvatar name={thread.title} src={thread.photo_url} colored className="size-9 shrink-0" fallbackClassName="text-[11px] font-bold" />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className={cn("truncate text-sm text-dash-ink", thread.unread_count ? "font-semibold" : "font-medium")}>{thread.title}</span>
                  <span className="shrink-0 text-[11px] text-dash-faint">{previewTime(last?.created_at)}</span>
                </span>
                <span className="block truncate text-[11px] uppercase tracking-[0.08em] text-dash-faint">{thread.subtitle}</span>
                <span className="mt-0.5 flex items-center justify-between gap-2">
                  <span className={cn("truncate text-xs", thread.unread_count ? "text-dash-ink" : "text-dash-muted")}>{preview}</span>
                  {thread.unread_count > 0 && (
                    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-white" aria-label={`${thread.unread_count} unread`}>
                      {thread.unread_count > 99 ? "99+" : thread.unread_count}
                    </span>
                  )}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ============ Conversation ============
function MessageBubble({ message, mine, showSender, showTime, onRetry, onDiscard }: {
  message: ChatMessage;
  mine: boolean;
  showSender: boolean;
  showTime: boolean;
  onRetry: (m: ChatMessage) => void;
  onDiscard: (m: ChatMessage) => void;
}) {
  return (
    <div className={cn("flex flex-col", mine ? "items-end" : "items-start")} data-testid="chat-message">
      {showSender && (
        <span className="mb-1 px-1 text-[11px] font-medium text-dash-muted">{message.sender_name || "Team member"}</span>
      )}
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm leading-6 sm:max-w-[70%]",
          mine ? "rounded-br-md bg-primary text-white" : "rounded-bl-md bg-dash-searchBg text-dash-ink",
          message.pending && "opacity-70",
          message.failed && "bg-dash-rust/10 text-dash-ink ring-1 ring-dash-rust/40",
        )}
      >
        <Linkified text={message.body} mine={mine && !message.failed} />
      </div>
      {(showTime || message.pending || message.failed) && (
      <span className="mt-1 px-1 text-[10px] text-dash-faint">
        {message.failed ? (
          <span className="flex items-center gap-2 text-dash-rust">
            Not sent
            <button type="button" onClick={() => onRetry(message)} className="inline-flex items-center gap-1 font-medium underline underline-offset-2" data-testid="chat-retry">
              <RotateCw className="size-3" /> Retry
            </button>
            <button type="button" onClick={() => onDiscard(message)} className="font-medium text-dash-muted underline underline-offset-2">Discard</button>
          </span>
        ) : message.pending ? "Sending…" : timeOf(message.created_at)}
      </span>
      )}
    </div>
  );
}

function Conversation({ dealId, thread, viewer, socketStatus, readOnlyReason, onBack, showBack, backClass }: {
  dealId: string;
  thread: ChatThread;
  viewer: ChatViewer;
  socketStatus: ChatSocketStatus;
  readOnlyReason: string | null;
  onBack: () => void;
  showBack: boolean;
  backClass: string;
}) {
  const threadId = thread.id;
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useChatMessages(dealId, threadId, socketStatus);
  const { outbox, send, retry, discard } = useSendChatMessage(dealId, threadId, viewer);
  const { mutate: markRead, isPending: markingRead } = useMarkChatRead(dealId);
  const draftKey = `${dealId}:${threadId}`;
  const [text, setText] = useState(() => drafts.get(draftKey) || "");
  const [showJump, setShowJump] = useState(false);
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nearBottom = useRef(true);
  const olderAnchor = useRef<number | null>(null);

  const stored = useMemo(() => flattenMessages(data), [data]);
  const messages = useMemo(() => {
    // The socket echo can land before the POST resolves; the stored row wins.
    const confirmed = new Set(stored.map((m) => m.client_id).filter(Boolean));
    return [...stored, ...outbox.filter((m) => !confirmed.has(m.client_id))];
  }, [stored, outbox]);
  const lastId = messages[messages.length - 1]?.id;
  const firstId = messages[0]?.id;

  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  // No toast for a message landing in the conversation being read.
  useChatThreadOnScreen(dealId, threadId, visible);

  // Reading is only reading when the thread is on screen in a visible tab.
  useEffect(() => {
    if (visible && thread.unread_count > 0 && !markingRead && !isLoading) markRead(threadId);
  }, [visible, thread.unread_count, threadId, markingRead, isLoading, markRead]);

  // Keep the reader pinned to the newest message unless they have scrolled up
  // to read history — then offer a jump instead of yanking them down.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (olderAnchor.current !== null) return;
    const mineLast = messages[messages.length - 1]?.sender_id === viewer.id;
    if (nearBottom.current || mineLast) {
      el.scrollTop = el.scrollHeight;
      setShowJump(false);
    } else {
      setShowJump(true);
    }
  }, [lastId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading an older page must not move what the reader is looking at.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || olderAnchor.current === null) return;
    el.scrollTop += el.scrollHeight - olderAnchor.current;
    olderAnchor.current = null;
  }, [firstId]);

  useEffect(() => {
    nearBottom.current = true;
    setShowJump(false);
    inputRef.current?.focus({ preventScroll: true });
  }, [threadId]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (nearBottom.current) setShowJump(false);
  };

  const loadOlder = () => {
    if (!scrollRef.current) return;
    olderAnchor.current = scrollRef.current.scrollHeight;
    fetchNextPage().catch(() => { olderAnchor.current = null; });
  };

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setShowJump(false);
  };

  const updateText = (value: string) => {
    setText(value);
    if (value) drafts.set(draftKey, value);
    else drafts.delete(draftKey);
  };

  // Grow with the text up to a handful of lines, then scroll inside the box.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    el.style.overflowY = el.scrollHeight > 160 ? "auto" : "hidden";
  }, [text]);

  const trimmed = text.trim();
  const tooLong = trimmed.length > MESSAGE_MAX_CHARS;
  const canSend = Boolean(trimmed) && !tooLong && !readOnlyReason;

  const submit = () => {
    if (!canSend) return;
    nearBottom.current = true;
    send(trimmed);
    updateText("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // `isComposing` keeps Enter from sending half-typed CJK/IME input.
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="flex flex-col gap-4 p-4" aria-busy="true">
        {[60, 40, 72, 52].map((w, i) => (
          <div key={i} className={cn("h-9 animate-pulse rounded-2xl bg-dash-searchBg", i % 2 ? "self-end" : "self-start")} style={{ width: `${w}%` }} />
        ))}
      </div>
    );
  } else if (isError && !data) {
    // Only when nothing ever loaded: a failed background poll while offline
    // must not replace a conversation that is already on screen.
    body = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-dash-muted">This conversation couldn’t be loaded.</p>
        <Button type="button" variant="dashboardOutline" size="sm" onClick={() => refetch()}>Try again</Button>
      </div>
    );
  } else if (!messages.length) {
    body = (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center" data-testid="chat-empty-thread">
        <span className="flex size-11 items-center justify-center rounded-xl bg-dash-hover text-dash-greenDeep" aria-hidden="true">
          <MessagesSquare className="size-5" />
        </span>
        <p className="text-sm font-semibold text-dash-ink">Start the conversation</p>
        <p className="max-w-sm text-sm text-dash-muted">
          {thread.is_mine
            ? `Send ${thread.title} a message — it arrives the moment they’re online.`
            : `Say hello to ${thread.title}. They’ll see it live, or next time they open this deal.`}
        </p>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-2 px-4 py-4">
        {hasNextPage && (
          <div className="mb-2 flex justify-center">
            <Button type="button" variant="dashboardOutline" size="sm" onClick={loadOlder} loading={isFetchingNextPage} data-testid="chat-load-older">
              Load earlier messages
            </Button>
          </div>
        )}
        {messages.map((message, index) => {
          const prev = messages[index - 1];
          const mine = message.sender_id === viewer.id;
          const newDay = !prev || dayKey(prev.created_at) !== dayKey(message.created_at);
          const next = messages[index + 1];
          const newRun = newDay || prev.sender_id !== message.sender_id;
          // One timestamp per burst: the last bubble before the sender changes
          // or the conversation pauses for more than five minutes.
          const showTime = !next || next.sender_id !== message.sender_id
            || new Date(next.created_at).getTime() - new Date(message.created_at).getTime() > 5 * 60_000;
          return (
            <Fragment key={message.id}>
              {newDay && (
                <div className="my-2 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.12em] text-dash-faint">
                  <span className="h-px flex-1 bg-dash-border" />
                  {dayLabel(message.created_at)}
                  <span className="h-px flex-1 bg-dash-border" />
                </div>
              )}
              <div className={cn(newRun && !newDay && "mt-2")}>
                <MessageBubble message={message} mine={mine} showSender={!mine && newRun} showTime={showTime} onRetry={retry} onDiscard={discard} />
              </div>
            </Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-testid="chat-conversation">
      <header className="flex items-center gap-3 border-b border-dash-border px-4 py-3">
        {showBack && (
          <Button type="button" variant="seamless" onClick={onBack} className={cn("-ml-1 p-1 text-dash-muted hover:text-dash-ink", backClass)} aria-label="Back to conversations">
            <ChevronLeft className="size-5" />
          </Button>
        )}
        <UserAvatar name={thread.title} src={thread.photo_url} colored className="size-9 shrink-0" fallbackClassName="text-[11px] font-bold" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-dash-ink">{thread.title}</p>
          <p className="flex items-center gap-1 truncate text-xs text-dash-muted">
            <Lock className="size-3 shrink-0" aria-hidden="true" /> {privacyLine(thread, viewer)}
          </p>
        </div>
        <StatusPill status={socketStatus} />
      </header>

      <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} onScroll={onScroll} className="absolute inset-0 flex flex-col overflow-y-auto" data-testid="chat-scroll">
          {body}
        </div>
        {showJump && (
          <button
            type="button"
            onClick={jumpToLatest}
            className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-dash-ink px-3 py-1.5 text-xs font-medium text-white shadow-md"
            data-testid="chat-jump-latest"
          >
            <ArrowDown className="size-3.5" /> New messages
          </button>
        )}
      </div>

      <div className="border-t border-dash-border p-3">
        {readOnlyReason ? (
          <p className="rounded-xl bg-dash-searchBg px-4 py-3 text-center text-xs text-dash-muted" data-testid="chat-read-only">{readOnlyReason}</p>
        ) : (
          <>
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                value={text}
                onChange={(event) => updateText(event.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={`Message ${thread.title}…`}
                aria-label={`Message ${thread.title}`}
                className="min-h-10 resize-none py-2.5 leading-5"
                data-testid="chat-input"
              />
              <Button
                type="button"
                variant="dashboardPrimary"
                onClick={submit}
                disabled={!canSend}
                className="h-10 shrink-0 px-3.5"
                aria-label="Send message"
                data-testid="chat-send"
              >
                <Send className="size-4" />
              </Button>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3 px-1 text-[11px] text-dash-faint">
              <span className="hidden sm:inline">Enter to send · Shift + Enter for a new line</span>
              {trimmed.length > COUNTER_FROM && (
                <span className={cn("ml-auto", tooLong && "font-medium text-dash-rust")}>
                  {trimmed.length.toLocaleString()} / {MESSAGE_MAX_CHARS.toLocaleString()}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============ Panel ============
/**
 * Realtime chat for one deal. Agents get a conversation per participant;
 * a participant usually has exactly one line (to their agent's team) and sees
 * it without a list in the way. A co-agent sees two: their line to the host
 * team and the one with their own client.
 */
export default function DealChat({ dealId, viewer, socketStatus, onInvite, className, splitAt = "lg" }: DealChatProps) {
  const split = SPLIT_CLASSES[splitAt];
  const { data, isLoading, isError, refetch } = useChatThreads(dealId, socketStatus);
  const threads = useMemo(() => data?.threads ?? [], [data]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const frameRef = useRef<HTMLElement>(null);
  const single = threads.length === 1;
  // Fall back to the newest conversation when nothing (or a vanished thread) is selected.
  const activeThread = threads.find((t) => t.id === selectedId) || threads[0] || null;

  const select = (id: string) => {
    setSelectedId(id);
    setMobileOpen(true);
  };

  // Bring the whole panel — composer included — on screen when the tab opens.
  // (Phones get a shorter frame: their header carries the search row too.)
  const ready = !isLoading && threads.length > 0;
  useEffect(() => {
    if (ready) frameRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [ready]);

  const frame = cn(
    "flex h-[min(680px,calc(100dvh-200px))] min-h-[440px] lg:h-[min(680px,calc(100dvh-140px))] overflow-hidden rounded-xl border border-dash-border bg-white",
    className,
  );

  if (isLoading) {
    return <div className={cn(frame, "animate-pulse bg-dash-searchBg/60")} aria-busy="true" data-testid="chat-loading" />;
  }
  if (isError && !data) {
    return (
      <EmptyState
        compact
        icon={MessagesSquare}
        title="Chat couldn’t be loaded"
        description="Check your connection and try again."
        action={<Button type="button" variant="dashboardOutline" size="sm" className="mt-4" onClick={() => refetch()}>Try again</Button>}
      />
    );
  }
  if (!threads.length) {
    return (
      <EmptyState
        compact
        icon={MessagesSquare}
        title="No conversations yet"
        description={viewer.internal
          ? "A private conversation opens with each client, lender, attorney and co-agent as soon as they accept their invitation to this deal."
          : "Chat opens once your agent has set up this deal room."}
        action={onInvite ? (
          <Button type="button" variant="dashboardPrimary" size="sm" className="mt-4" onClick={onInvite} data-testid="chat-invite">
            <UserPlus data-icon="inline-start" /> Invite someone
          </Button>
        ) : undefined}
        data-testid="chat-empty"
      />
    );
  }

  return (
    <section ref={frameRef} className={cn(frame, "scroll-mb-4")} data-testid="deal-chat">
      {!single && (
        <aside className={cn("w-full flex-col border-dash-border", split.list, mobileOpen ? "hidden" : "flex")}>
          <div className="border-b border-dash-border px-4 py-3">
            <h2 className="text-sm font-semibold text-dash-ink">Conversations</h2>
          </div>
          <ThreadList threads={threads} activeId={activeThread?.id ?? null} viewerId={viewer.id} onSelect={select} />
        </aside>
      )}
      {activeThread && (
        <div className={cn("min-w-0 flex-1 flex-col", split.pane, single || mobileOpen ? "flex" : "hidden")}>
          <Conversation
            key={activeThread.id}
            dealId={dealId}
            thread={activeThread}
            viewer={viewer}
            socketStatus={socketStatus}
            readOnlyReason={data?.read_only_reason ?? null}
            showBack={!single}
            backClass={split.back}
            onBack={() => setMobileOpen(false)}
          />
        </div>
      )}
    </section>
  );
}
